<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <button type="button" class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleUpdateConfig">
          <span v-if="submitting">儲存中...</span>
          <span v-else>儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="calendar-config-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchConfig">重試</button>
    </div>

    <div v-else class="config-content">
      <!-- OAuth 授權區域 -->
      <div v-if="!config" class="auth-section">
        <div class="auth-card">
          <h3 class="section-title">Google Calendar 授權</h3>
          <p class="auth-description">
            連結 Google Calendar 後，系統會自動將訂位同步至您的日曆中。
          </p>
          <button class="btn btn-primary btn-lg" @click="handleAuthorize">
            <i class="ri-google-fill"></i>
            連結 Google Calendar
          </button>
        </div>
      </div>

      <!-- 設定區域 -->
      <div v-else class="config-sections">
        <!-- 授權狀態 -->
        <div class="config-section">
          <h3 class="section-title">授權狀態</h3>
          <div class="status-grid">
            <div class="status-item">
              <label>Google 帳號</label>
              <span>{{ config.googleAccountEmail }}</span>
            </div>
            <div class="status-item">
              <label>日曆名稱</label>
              <span>{{ config.calendarName || '-' }}</span>
            </div>
            <div class="status-item">
              <label>同步狀態</label>
              <span :class="getSyncStatusBadgeClass(syncStatus.lastSyncStatus)">
                {{ getSyncStatusText(syncStatus.lastSyncStatus) }}
              </span>
            </div>
            <div class="status-item">
              <label>已同步事件數</label>
              <span>{{ syncStatus.totalEventsSynced || 0 }}</span>
            </div>
          </div>
          <div class="action-buttons">
            <button class="btn btn-danger" @click="handleRevoke">
              <i class="ri-links-line"></i>
              解除授權
            </button>
          </div>
        </div>

        <!-- 同步設定 -->
        <div class="config-section">
          <h3 class="section-title">同步設定</h3>
          <form @submit.prevent="handleUpdateConfig">
            <div class="form-row">
              <div class="form-group">
                <label>
                  <input
                    v-model="configForm.syncEnabled"
                    type="checkbox"
                  />
                  啟用自動同步
                </label>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="syncScope">同步範圍（天數）</label>
                <input
                  id="syncScope"
                  v-model.number="configForm.syncScope"
                  type="number"
                  class="form-control"
                  min="1"
                  max="365"
                />
                <small class="form-hint">同步未來多少天的訂位（1-365 天）</small>
              </div>

              <div class="form-group">
                <label for="reminderMinutes">提醒時間（分鐘）</label>
                <input
                  id="reminderMinutes"
                  v-model.number="configForm.reminderMinutes"
                  type="number"
                  class="form-control"
                  min="0"
                  max="1440"
                />
                <small class="form-hint">提前多少分鐘提醒（0-1440 分鐘）</small>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group full-width">
                <label for="eventTitleTemplate">事件標題範本</label>
                <input
                  id="eventTitleTemplate"
                  v-model="configForm.eventTitleTemplate"
                  type="text"
                  class="form-control"
                  placeholder="{status}: {name} ({guestCount}人) - {tableNumber}"
                />
                <small class="form-hint">
                  可用變數：{status}、{name}、{guestCount}、{tableNumber}
                </small>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>
                  <input
                    v-model="configForm.includeContactInfo"
                    type="checkbox"
                  />
                  包含聯絡資訊
                </label>
              </div>

              <div class="form-group">
                <label>
                  <input
                    v-model="configForm.enableReminders"
                    type="checkbox"
                  />
                  啟用提醒
                </label>
              </div>
            </div>
          </form>
        </div>

        <!-- 同步狀態 -->
        <div class="config-section">
          <h3 class="section-title">同步狀態</h3>
          <div class="status-grid">
            <div class="status-item">
              <label>最後同步時間</label>
              <span>{{ formatDateTime(syncStatus.lastSyncAt) }}</span>
            </div>
            <div class="status-item">
              <label>最後同步狀態</label>
              <span :class="getSyncStatusBadgeClass(syncStatus.lastSyncStatus)">
                {{ getSyncStatusText(syncStatus.lastSyncStatus) }}
              </span>
            </div>
            <div v-if="syncStatus.lastSyncError" class="status-item full-width">
              <label>最後同步錯誤</label>
              <span class="error-text">{{ syncStatus.lastSyncError }}</span>
            </div>
          </div>
          <div class="action-buttons">
            <button class="btn btn-info" @click="handleBatchSync">
              <i class="ri-refresh-line"></i>
              批次同步
            </button>
            <button class="btn btn-secondary" @click="handleViewLogs">
              <i class="ri-file-list-line"></i>
              查看同步紀錄
            </button>
          </div>
        </div>

        <!-- 日曆連結 -->
        <div class="config-section">
          <h3 class="section-title">日曆連結</h3>
          <div v-if="calendarLinks" class="links-grid">
            <div class="link-item">
              <label>網頁版日曆</label>
              <a :href="calendarLinks.webLink" target="_blank" class="btn btn-sm btn-secondary">
                <i class="ri-external-link-line"></i>
                開啟
              </a>
            </div>
            <div class="link-item">
              <label>手機版日曆</label>
              <a :href="calendarLinks.mobileLink" target="_blank" class="btn btn-sm btn-secondary">
                <i class="ri-external-link-line"></i>
                開啟
              </a>
            </div>
            <div class="link-item full-width">
              <label>訂閱連結（iCal）</label>
              <div class="link-input-group">
                <input
                  :value="calendarLinks.subscribeLink"
                  type="text"
                  class="form-control"
                  readonly
                />
                <button class="btn btn-sm btn-secondary" @click="copyToClipboard(calendarLinks.subscribeLink)">
                  <i class="ri-file-copy-line"></i>
                  複製
                </button>
              </div>
            </div>
            <div class="link-item full-width">
              <label>嵌入代碼</label>
              <div class="link-input-group">
                <textarea
                  :value="calendarLinks.embedCode"
                  class="form-control"
                  rows="3"
                  readonly
                ></textarea>
                <button class="btn btn-sm btn-secondary" @click="copyToClipboard(calendarLinks.embedCode)">
                  <i class="ri-file-copy-line"></i>
                  複製
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { calendarService } from '@/api/modules/calendar.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'StoreCalendar',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('Google Calendar 設定');
    const config = ref(null);
    const syncStatus = ref({});
    const calendarLinks = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const submitting = ref(false);

    const configForm = ref({
      syncEnabled: true,
      syncScope: 30,
      eventTitleTemplate: '{status}: {name} ({guestCount}人) - {tableNumber}',
      includeContactInfo: true,
      enableReminders: true,
      reminderMinutes: 60
    });

    const fetchConfig = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await calendarService.getConfig(route.params.id);
        if (response.data?.success && response.data?.data) {
          config.value = response.data.data;
          configForm.value = {
            syncEnabled: config.value.syncEnabled ?? true,
            syncScope: config.value.syncScope ?? 30,
            eventTitleTemplate: config.value.eventTitleTemplate || '{status}: {name} ({guestCount}人) - {tableNumber}',
            includeContactInfo: config.value.includeContactInfo ?? true,
            enableReminders: config.value.enableReminders ?? true,
            reminderMinutes: config.value.reminderMinutes ?? 60
          };
          await fetchSyncStatus();
          await fetchCalendarLinks();
        } else {
          config.value = null;
        }
      } catch (err) {
        if (err.response?.status === 404) {
          config.value = null;
        } else {
          error.value = err.response?.data?.message || err.message || '查詢設定失敗';
        }
      } finally {
        loading.value = false;
      }
    };

    const fetchSyncStatus = async () => {
      try {
        const response = await calendarService.getSyncStatus(route.params.id);
        if (response.data?.success && response.data?.data) {
          syncStatus.value = response.data.data;
        }
      } catch (err) {
        console.error('Failed to fetch sync status:', err);
      }
    };

    const fetchCalendarLinks = async () => {
      try {
        const response = await calendarService.getCalendarLinks(route.params.id);
        if (response.data?.success && response.data?.data) {
          calendarLinks.value = response.data.data;
        }
      } catch (err) {
        console.error('Failed to fetch calendar links:', err);
      }
    };

    const handleAuthorize = async () => {
      try {
        const response = await calendarService.getAuthUrl(route.params.id);
        if (response.data?.success && response.data?.data?.authUrl) {
          window.location.href = response.data.data.authUrl;
        } else {
          throw new Error('取得授權 URL 失敗');
        }
      } catch (err) {
        showError(err.response?.data?.message || '取得授權 URL 失敗');
      }
    };

    const handleUpdateConfig = async () => {
      submitting.value = true;

      try {
        await calendarService.updateConfig(route.params.id, configForm.value);
        showSuccess('設定已更新');
        fetchConfig();
      } catch (err) {
        showError(err.response?.data?.message || '更新設定失敗');
      } finally {
        submitting.value = false;
      }
    };

    const handleBatchSync = async () => {
      const confirmed = await showDeleteConfirm('確定要執行批次同步嗎？這可能需要一些時間。');
      if (!confirmed) return;

      try {
        const today = new Date();
        const dateFrom = today.toISOString().split('T')[0];
        const dateTo = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        await calendarService.batchSync(route.params.id, {
          dateFrom,
          dateTo,
          syncType: 'all'
        });
        showSuccess('批次同步已開始');
        fetchSyncStatus();
      } catch (err) {
        showError(err.response?.data?.message || '批次同步失敗');
      }
    };

    const handleViewLogs = () => {
      // TODO: 開啟同步紀錄視窗或導向紀錄頁面
      showSuccess('同步紀錄功能開發中');
    };

    const handleRevoke = async () => {
      const confirmed = await showDeleteConfirm('確定要解除 Google Calendar 授權嗎？');
      if (!confirmed) return;

      try {
        // TODO: 實作解除授權 API
        showSuccess('授權已解除');
        fetchConfig();
      } catch (err) {
        showError(err.response?.data?.message || '解除授權失敗');
      }
    };

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        showSuccess('已複製到剪貼簿');
      } catch (err) {
        showError('複製失敗');
      }
    };

    const handleBack = () => {
      router.push(`/client/stores/${route.params.id}`);
    };

    const getSyncStatusText = (status) => {
      const statusMap = {
        'success': '成功',
        'failed': '失敗',
        'pending': '待同步'
      };
      return statusMap[status] || status || '未知';
    };

    const getSyncStatusBadgeClass = (status) => {
      const classMap = {
        'success': 'badge badge-success',
        'failed': 'badge badge-danger',
        'pending': 'badge badge-warning'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDateTime = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW');
    };

    // 檢查是否有 OAuth 回調參數
    const checkOAuthCallback = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      if (error) {
        showError('授權失敗：' + error);
        // 清除 URL 參數
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (code) {
        handleOAuthCallback(code);
      }
    };

    const handleOAuthCallback = async (code) => {
      try {
        await calendarService.authorize(route.params.id, code);
        showSuccess('Google Calendar 授權成功');
        // 清除 URL 參數
        window.history.replaceState({}, document.title, window.location.pathname);
        fetchConfig();
      } catch (err) {
        showError(err.response?.data?.message || '授權處理失敗');
      }
    };

    onMounted(() => {
      checkOAuthCallback();
      fetchConfig();
    });

    return {
      pageTitle,
      config,
      syncStatus,
      calendarLinks,
      loading,
      error,
      submitting,
      configForm,
      fetchConfig,
      handleAuthorize,
      handleUpdateConfig,
      handleBatchSync,
      handleViewLogs,
      handleRevoke,
      copyToClipboard,
      handleBack,
      getSyncStatusText,
      getSyncStatusBadgeClass,
      formatDateTime
    };
  }
};
</script>

<style scoped>
.calendar-config-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.config-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.auth-section {
  display: flex;
  justify-content: center;
  padding: 40px 20px;
}

.auth-card {
  background: white;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 500px;
}

.auth-description {
  margin: 16px 0 24px;
  color: #666;
}

.config-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.config-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.status-item {
  display: flex;
  flex-direction: column;
}

.status-item.full-width {
  grid-column: 1 / -1;
}

.status-item label {
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
  font-size: 14px;
}

.status-item span {
  font-size: 16px;
}

.status-item .error-text {
  color: #dc3545;
  font-size: 14px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  margin-bottom: 6px;
  font-weight: 500;
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.action-buttons {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.links-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.link-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.link-item.full-width {
  width: 100%;
}

.link-item label {
  font-weight: 500;
  color: #666;
  font-size: 14px;
}

.link-input-group {
  display: flex;
  gap: 8px;
}

.link-input-group .form-control {
  flex: 1;
}

@media (max-width: 768px) {
  .status-grid,
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
